import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiStar,
  FiPackage,
  FiGlobe,
  FiShield,
  FiTrendingUp,
  FiMessageCircle,
  FiZap,
  FiUsers,
} from "react-icons/fi";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import ProcessSection from "../components/ProcessSection";
import "./Home.css";
import judoGi from '../assets/products/judu-gi.jpeg';
import mma1 from '../assets/products/mma-shorts/mma-shorts.jpeg';
import rash2 from '../assets/products/rash-guards/rash-guard2.jpeg';
import belt1 from '../assets/products/belts/belt1.jpeg';
import hoodie4 from '../assets/products/sports-hoodies/sports-hoodies6.jpeg';

const stats = [
  {
    value: 5,
    suffix: "+",
    label: "Years of Excellence",
    icon: <FiTrendingUp size={22} />,
    color: "#c9a227",
  },
  {
    value: 50,
    suffix: "+",
    label: "Qualified Staff",
    icon: <FiUsers size={22} />,
    color: "#5b9bd5",
  },
  {
    value: 500,
    suffix: "+",
    label: "Satisfied Clients",
    icon: <FiStar size={22} />,
    color: "#4caf7d",
  },
  {
    value: 40,
    suffix: "+",
    label: "Worldwide Delivery",
    icon: <FiGlobe size={22} />,
    color: "#e07b5c",
  },
];

const categories = [
  {
    id: "uniforms",
    name: "Uniforms",
    tag: "IJF Style",
    path: "/uniforms/judo-gi",
    img: judoGi,
    accent: "#c9a227",
  },
  {
    id: "sports-hoodies",
    name: "Sports Hoodies",
    tag: "Custom & Full Sublimation",
    path: "/products/sports-hoodies",
    img: hoodie4,
    accent: "#4caf7d",
  },
  {
    id: "mma-shorts",
    name: "MMA Shorts",
    tag: "Fight Ready",
    path: "/products/mma-shorts",
    img: mma1,
    accent: "#5b9bd5",
  },
  { id: 'rash-guards', name: 'Rash Guards', tag: 'Full & Short', path: '/products/rash-guards', img: rash2, accent: '#9b59b6' },
  {
    id: "belts",
    name: "Belts & Accessories",
    tag: "All Ranks",
    path: "/products/belts",
    img: belt1,
    accent: "#c9a227",
  },
];

const testimonials = [
  {
    name: "Carlos Mendoza",
    role: "BJJ Academy Owner",
    country: "Brazil",
    flag: "🇧🇷",
    stars: 5,
    text: "Pearl Weave quality is outstanding. Our 40-member academy has been ordering from Shaheen Sports for 3 years — consistent quality, on-time delivery every single time.",
  },
  {
    name: "James Thornton",
    role: "Head Coach",
    country: "United Kingdom",
    flag: "🇬🇧",
    stars: 5,
    text: "Best OEM partner we have found. Complete customization from GSM to packaging. Our brand looks premium and students love the fit and durability.",
  },
  {
    name: "Ahmed Al-Rashidi",
    role: "National Federation",
    country: "UAE",
    flag: "🇦🇪",
    stars: 5,
    text: "We supply our national Judo team. The Touchio 750 GSM Gi meets every requirement. Shaheen Sports never compromises on quality.",
  },
  {
    name: "Marcus Weber",
    role: "MMA Gym Owner",
    country: "Germany",
    flag: "🇩🇪",
    stars: 5,
    text: "MMA shorts and rash guards are exactly what we needed. Full sublimation, great stretch, perfect finishing. Will reorder without hesitation.",
  },
];

const whyUs = [
  {
    icon: <FiShield size={24} />,
    title: "5+ Years of Experience",
    desc: "Founded in 2020 — decades of hands-on manufacturing expertise in every product we make.",
  },
  {
    icon: <FiGlobe size={24} />,
    title: "Exports Worldwide",
    desc: "Trusted by academies, brands and federations across 6 continents worldwide.",
  },
  {
    icon: <FiPackage size={24} />,
    title: "Full Custom OEM/ODM",
    desc: "Your fabric, your GSM, your colors, your branding — produced end-to-end under our roof.",
  },
  {
    icon: <FiZap size={24} />,
    title: "In-House Fabric Weaving",
    desc: "We manufacture Pearl, Gold, Crystal and Diamond Weave fabrics ourselves.",
  },
  {
    icon: <FiUsers size={24} />,
    title: "50+ Qualified Staff",
    desc: "Experienced team of 50+ specialists ensuring precision in every order.",
  },
  {
    icon: <FiCheckCircle size={24} />,
    title: "Zero-Compromise QC",
    desc: "Multi-point inspection on every unit. If it does not pass, it does not ship.",
  },
];

const clients = [
  { name: "Brazil", flag: "🇧🇷" },
  { name: "USA", flag: "🇺🇸" },
  { name: "UK", flag: "🇬🇧" },
  { name: "Germany", flag: "🇩🇪" },
  { name: "UAE", flag: "🇦🇪" },
  { name: "Japan", flag: "🇯🇵" },
  { name: "Canada", flag: "🇨🇦" },
  { name: "France", flag: "🇫🇷" },
  { name: "Australia", flag: "🇦🇺" },
  { name: "Netherlands", flag: "🇳🇱" },
  { name: "Sweden", flag: "🇸🇪" },
  { name: "Italy", flag: "🇮🇹" },
];

const faqs = [
  {
    q: "What is the minimum order quantity?",
    a: "MOQ starts from 10 pieces per style per color for Gis and uniforms. Belts start from 50 pcs, accessories from 100 pcs.",
  },
  {
    q: "Can I get custom colors and embroidery?",
    a: "Yes — we offer full customization including fabric color, GSM selection, embroidery, screen printing, sublimation, patch placement and custom packaging.",
  },
  {
    q: "How long does production take?",
    a: "Samples are ready in 2–3 weeks. Bulk production takes 4–6 weeks depending on order size and customization complexity.",
  },
  {
    q: "Do you offer private label / OEM?",
    a: "Absolutely. We are a full OEM/ODM manufacturer. We produce under your brand with custom labels, hangtags and packaging.",
  },
  {
    q: "Which countries do you export to?",
    a: "We Exporting Worldwide including USA, UK, Brazil, Germany, UAE, Japan, Australia, Canada, France and many more.",
  },
  {
    q: "Are your products competition legal?",
    a: "Yes — our BJJ Gis are IBJJF-legal, Judo Gis are IJF-style.",
  },
];

export default function Home() {
  const { ref: statsRef, inView: statsInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const { ref: whyRef, inView: whyInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { ref: catRef, inView: catInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [activeT, setActiveT] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const waMsg = encodeURIComponent(
    "Hi Shaheen Sports! I'd like to get a quote for your products.",
  );

  useEffect(() => {
    const t = setInterval(
      () => setActiveT((p) => (p + 1) % testimonials.length),
      5500,
    );
    return () => clearInterval(t);
  }, []);

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        <div className="hero__slides">
          {[
            "https://images.unsplash.com/photo-1555597673-b21d5c935865?auto=format&fit=crop&w=1920&q=80",
            "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1920&q=80",
            "https://images.unsplash.com/photo-1517438322307-e67111335449?auto=format&fit=crop&w=1920&q=80",
          ].map((img, i) => (
            <div
              key={i}
              className="hero__slide"
              style={{ backgroundImage: `url(${img})` }}
            />
          ))}
        </div>
        <div className="hero__overlay" />
        <div className="hero__particles">
          {Array(6)
            .fill(null)
            .map((_, i) => (
              <div key={i} className={`particle particle--${i + 1}`} />
            ))}
        </div>
        <div className="container hero__content">
          <div className="hero__eyebrow">
            <span className="hero__cert-pill">Est. 2020</span>
            <span className="hero__cert-pill">
              <FiGlobe size={12} /> Worldwide
            </span>
            <span className="hero__cert-pill">
              <FiUsers size={12} /> 500+ Clients
            </span>
          </div>
          <h1 className="hero__title">
            World-Class
            <br />
            <span className="hero__title--gold">Martial Arts Gear</span>
          </h1>
          <p className="hero__sub">
            Premium manufacturer & exporter of Judo Gis, Jiujitsu Gis, Karate
            Gis, MMA Shorts, Rash Guards and martial arts accessories — trusted
            by <strong>500+ academies</strong> across <strong>Worldwide</strong>
            .
          </p>
          <div className="hero__actions">
            <Link to="/uniforms" className="btn-primary hero__btn-main">
              Explore Products <FiArrowRight size={16} />
            </Link>
            <a
              href={`https://wa.me/923420757213?text=${waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa"
            >
              <FiMessageCircle size={16} /> WhatsApp Us
            </a>
          </div>
          <div className="hero__trust">
            <div className="hero__trust-item">
              <FiCheckCircle size={14} />
              MOQ From 10 Pieces
            </div>
            <div className="hero__trust-item">
              <FiCheckCircle size={14} />
              24hr Response
            </div>
            <div className="hero__trust-item">
              <FiCheckCircle size={14} />
              Est. 2020
            </div>
          </div>
        </div>
        <div className="hero__scroll-cue">
          <div className="hero__scroll-line" />
          <span>Scroll</span>
        </div>
        <div className="hero__shape" />
      </section>

      {/* TRUST BAR */}
      <div className="trust-bar">
        <div className="container trust-bar__inner">
          {[
            { icon: <FiShield size={16} />, text: "5+ Years Manufacturing" },
            { icon: <FiGlobe size={16} />, text: "Exporting Worldwide" },
            { icon: <FiPackage size={16} />, text: "Full OEM / Private Label" },
            {
              icon: <FiCheckCircle size={16} />,
              text: "In-House Fabric Weaving",
            },
            { icon: <FiUsers size={16} />, text: "500+ Satisfied Clients" },
          ].map((t, i) => (
            <div key={i} className="trust-bar__item">
              <span className="trust-bar__icon">{t.icon}</span>
              <span>{t.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* STATS */}
      <section className="stats-section" ref={statsRef}>
        <div className="container stats-grid">
          {stats.map((s, i) => (
            <div key={i} className="stat-card" style={{ "--accent": s.color }}>
              <div className="stat-card__icon">{s.icon}</div>

              {/* This check makes sure the number only renders if s.value exists */}
              {s.value && (
                <div className="stat-card__value">
                  {statsInView ? (
                    <CountUp end={s.value} duration={2.4} suffix={s.suffix} />
                  ) : (
                    "0"
                  )}
                </div>
              )}

              <div className="stat-card__label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="about-strip">
        <div className="container about-strip__inner">
          {/* Here is the updated code for the image frame and badge */}
          <div className="about-img-frame" style={{ position: "relative" }}>
            <img
              src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=900&q=80"
              alt="Shaheen Sports Manufacturing"
              className="about-img-main"
              loading="lazy"
            />
            <div
              className="about-img-badge"
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                bottom: "auto",
                zIndex: 10,
              }}
            >
              <strong>5+</strong>
              <small>Years</small>
            </div>
            <div className="about-stat-pills">
              <div className="about-pill">
                <strong>50+</strong>
                <span>Staff</span>
              </div>
              <div className="about-pill">
                <strong>🌍</strong>
                <span>Worldwide</span>
              </div>
              <div className="about-pill">
                <strong>500+</strong>
                <span>Clients</span>
              </div>
            </div>
          </div>
          <div className="about-strip__text">
            <span className="section-label">Who We Are</span>
            <h2 className="section-title">
              Pakistan's Most Trusted Martial Arts Manufacturer
            </h2>
            <div className="divider-gold" />
            <p>
              Founded in 2020 in Shaheen Sports is a vertically integrated
              manufacturer and exporter of professional martial arts uniforms,
              belts, MMA gear and custom sportswear, serving clients worldwide
              worldwide.
            </p>
            <p
              style={{
                marginTop: "1rem",
                color: "var(--dark-gray)",
                lineHeight: 1.8,
              }}
            >
              Every stage — fabric weaving, dyeing, cutting, stitching,
              printing, QC and packing — happens under one roof, giving us
              complete quality control.
            </p>
            <div className="about-checklist">
              {[
                "In-house Pearl, Gold, Crystal & Diamond Weave fabrics",
                "Complete OEM / private label capability",
                "IBJJF, IJF and WKF style products available",
              ].map((t) => (
                <div key={t} className="about-check">
                  <div className="about-check__dot" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
            <div className="about-strip__actions">
              <Link to="/about" className="btn-dark">
                Our Story <FiArrowRight size={15} />
              </Link>
              <Link to="/uniforms" className="btn-primary">
                Browse Products <FiArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="cat-section" ref={catRef}>
        <div className="container">
          <div className="cat-section__header">
            <div>
              <span className="section-label">Our Products</span>
              <h2 className="section-title">
                Premium Martial Arts Collections
              </h2>
              <div className="divider-gold" />
            </div>
            <Link to="/all-products" className="btn-dark">
              All Products <FiArrowRight size={15} />
            </Link>
          </div>
          <div className="cat-grid">
            {categories.map((cat, i) => (
              <Link
                key={cat.id}
                to={cat.path}
                className={`cat-card ${catInView ? "cat-card--visible" : ""}`}
                style={{
                  animationDelay: `${i * 0.08}s`,
                  "--border-color": cat.accent,
                }}
              >
                <div className="cat-card__img">
                  <img src={cat.img} alt={cat.name} loading="lazy" />
                </div>
                <div className="cat-card__overlay" />
                <div className="cat-card__body">
                  <span
                    className="cat-card__tag"
                    style={{ background: cat.accent, color: "#0b0c1e" }}
                  >
                    {cat.tag}
                  </span>
                  <h3 className="cat-card__name">{cat.name}</h3>
                  <div className="cat-card__cta" style={{ color: cat.accent }}>
                    View Products <FiArrowRight size={14} />
                  </div>
                </div>
                <div className="cat-card__hover-border" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="why-section" ref={whyRef}>
        <div className="why-section__bg" />
        <div className="container">
          <div className="why-header">
            <span className="section-label" style={{ color: "var(--gold)" }}>
              Why Choose Us
            </span>
            <h2 className="section-title" style={{ color: "var(--white)" }}>
              Built on Trust, Crafted with Precision
            </h2>
            <div className="divider-gold" />
            <p
              className="section-subtitle"
              style={{ color: "rgba(255,255,255,0.65)" }}
            >
              We don't just manufacture — we build long-term partnerships.
              Quality is not a department. It is our entire culture.
            </p>
          </div>
          <div className="why-grid">
            {whyUs.map((item, i) => (
              <div
                key={i}
                className={`why-card ${whyInView ? "why-card--visible" : ""}`}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="why-card__icon">{item.icon}</div>
                <h4 className="why-card__title">{item.title}</h4>
                <p className="why-card__desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <ProcessSection />

      {/* COUNTRIES */}
      <section className="countries-section">
        <div className="container">
          <div className="countries-header">
            <span className="section-label">Global Reach</span>
            <h2 className="section-title">Trusted worldwide</h2>
            <div className="divider-gold" />
          </div>
          <div className="countries-track-wrap">
            <div className="countries-track">
              {[...clients, ...clients].map((c, i) => (
                <div key={i} className="country-chip">
                  <span className="country-chip__flag">{c.flag}</span>
                  <span>{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testi-section">
        <div className="container">
          <div className="testi-header">
            <span className="section-label">Client Testimonials</span>
            <h2 className="section-title">What Our Clients Say</h2>
            <div className="divider-gold" />
          </div>
          <div className="testi-slider">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`testi-card ${i === activeT ? "testi-card--active" : ""}`}
              >
                <div className="testi-card__quote">"</div>
                <div className="testi-card__stars">
                  {Array(t.stars)
                    .fill(null)
                    .map((_, s) => (
                      <FiStar
                        key={s}
                        size={16}
                        fill="#c9a227"
                        color="#c9a227"
                      />
                    ))}
                </div>
                <p className="testi-card__text">{t.text}</p>
                <div className="testi-card__author">
                  <div className="testi-card__avatar">{t.name[0]}</div>
                  <div>
                    <strong>{t.name}</strong>
                    <span>
                      {t.role} · {t.flag} {t.country}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="testi-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                className={`testi-dot ${i === activeT ? "testi-dot--active" : ""}`}
                onClick={() => setActiveT(i)}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container faq-inner">
          <div className="faq-left">
            <span className="section-label">FAQs</span>
            <h2 className="section-title">Common Questions</h2>
            <div className="divider-gold" />
            <p className="section-subtitle">
              Have a different question? Reach us on WhatsApp — we respond
              within 2 hours.
            </p>
            <a
              href="https://wa.me/923420757213?text=Hi! I have a question."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa"
              style={{ marginTop: "2rem" }}
            >
              <FiMessageCircle size={16} /> Ask on WhatsApp
            </a>
          </div>
          <div className="faq-right">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`faq-item ${openFaq === i ? "faq-item--open" : ""}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="faq-item__q">
                  <span>{faq.q}</span>
                  <span className="faq-item__icon">
                    {openFaq === i ? "−" : "+"}
                  </span>
                </div>
                <div className="faq-item__a">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final-cta">
        <div className="final-cta__bg" />
        <div className="container final-cta__inner">
          <div className="final-cta__badge">
            <FiZap size={18} /> Ready to Order?
          </div>
          <h2 className="final-cta__title">Start Your Custom Order Today</h2>
          <p className="final-cta__sub">
            Send us your design, quantity and size breakdown. We respond within
            24 hours with full pricing.
          </p>
          <div className="final-cta__actions">
            <a
              href={`https://wa.me/923420757213?text=${encodeURIComponent("Hi Shaheen Sports! I want to place an order.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="final-cta__wa"
            >
              <FiMessageCircle size={20} /> Order via WhatsApp
            </a>
            <Link to="/contact" className="btn-outline">
              Send Inquiry <FiArrowRight size={16} />
            </Link>
          </div>
          <div className="final-cta__trust">
            <span>
              <FiCheckCircle size={13} />
              MOQ from 10 pcs
            </span>
            <span>
              <FiCheckCircle size={13} />
              4–6 Week Delivery
            </span>
            <span>
              <FiCheckCircle size={13} />
              Full OEM Available
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
